import { toast } from 'sonner';
import { fillablePdf, chooseFillMode } from './fillable';
import { drawTitledHeader } from './agencyInfo';

// Ivory Health Care branding colors
const BRAND_COLOR = [243, 156, 18]; // Orange #f39c12
const HEADER_COLOR = [30, 58, 95]; // Dark blue

/**
 * Generate PDF for Admission Consent Form
 */
export const generateAdmissionConsentPDF = (formData) => {
  const doc = fillablePdf();
  doc.patientInfo = { patientId: formData.patientId, name: formData.patientName, dob: formData.dateOfBirth || formData.patientDOB, mrn: formData.medicalRecordNumber || formData.mrn, dx: formData.primaryDiagnosis || formData.diagnosis };
  const pageWidth = doc.internal.pageSize.getWidth();
  let y = 20;

  // Header
  drawTitledHeader(doc, 'Admission Consent Form');
  
  y = 45;
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(10);

  // Patient Info Section
  doc.setFillColor(240, 240, 240);
  doc.rect(15, y, pageWidth - 30, 20, 'F');
  doc.setFont('helvetica', 'bold');
  doc.text('PATIENT INFORMATION', 20, y + 7);
  doc.setFont('helvetica', 'normal');
  doc.text(`Patient Name: ${formData.patientName || '_______________'}`, 20, y + 15);
  doc.text(`Date of Birth: ${formData.patientDOB || '_______________'}`, 120, y + 15);
  y += 30;

  // Acknowledgments Section
  const sections = [
    { 
      title: 'PATIENT RIGHTS AND RESPONSIBILITIES',
      checked: formData.receivedRightsResponsibilities,
      text: 'I acknowledge receipt of Patient Rights and Responsibilities'
    },
    { 
      title: 'CONSENT FOR TREATMENT',
      checked: formData.consentForTreatment,
      text: 'I consent to treatment as described'
    },
    { 
      title: 'RELEASE OF INFORMATION',
      checked: formData.releaseOfInformation,
      text: 'I authorize release of medical information'
    },
    { 
      title: 'FINANCIAL AGREEMENT',
      checked: formData.financialAgreement,
      text: 'I agree to the financial terms'
    },
    { 
      title: 'ASSIGNMENT OF BENEFITS',
      checked: formData.assignmentOfBenefits,
      text: 'I authorize assignment of insurance benefits'
    }
  ];

  sections.forEach(section => {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text(section.title, 20, y);
    y += 6;
    
    // Checkbox
    doc.rect(20, y - 4, 4, 4);
    if (section.checked) {
      doc.setFillColor(...BRAND_COLOR);
      doc.rect(20.5, y - 3.5, 3, 3, 'F');
    }
    
    doc.setFont('helvetica', 'normal');
    doc.text(section.text, 28, y);
    y += 10;
  });

  // Advance Directives
  y += 5;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('ADVANCE DIRECTIVES', 20, y);
  y += 8;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  
  doc.text(`Living Will: ${formData.hasLivingWill === true ? 'Yes' : formData.hasLivingWill === false ? 'No' : 'N/A'}`, 20, y);
  doc.text(`Healthcare POA: ${formData.hasHealthCarePOA === true ? 'Yes' : formData.hasHealthCarePOA === false ? 'No' : 'N/A'}`, 80, y);
  doc.text(`DNR Order: ${formData.hasDNR === true ? 'Yes' : formData.hasDNR === false ? 'No' : 'N/A'}`, 140, y);
  y += 8;

  if (formData.poaName) {
    doc.text(`POA Name: ${formData.poaName}`, 20, y);
    doc.text(`POA Phone: ${formData.poaPhone || 'N/A'}`, 100, y);
    y += 8;
  }

  // Signatures Section
  y += 10;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('SIGNATURES', 20, y);
  y += 10;
  
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  
  // Patient Signature
  doc.text('Patient Signature:', 20, y);
  doc.line(60, y, 130, y);
  doc.text(formData.patientSignature || '', 62, y - 2);
  doc.text('Date:', 135, y);
  doc.line(150, y, 190, y);
  doc.valueMode = true;  // a field, so the Blank copy leaves the date empty
  doc.text(new Date().toLocaleDateString(), 152, y - 2);
  doc.valueMode = false;
  y += 15;

  // Responsible Person Signature
  doc.text('Responsible Person:', 20, y);
  doc.line(60, y, 130, y);
  doc.text(formData.responsiblePersonSignature || '', 62, y - 2);
  doc.text('Relationship:', 135, y);
  doc.line(160, y, 190, y);
  doc.text(formData.responsiblePersonRelationship || '', 162, y - 2);
  y += 15;

  // Representative Signature
  doc.text('HH Representative:', 20, y);
  doc.line(60, y, 130, y);
  doc.text(formData.representativeSignature || '', 62, y - 2);

  // Footer
  doc.setFontSize(8);
  doc.setTextColor(128, 128, 128);
  doc.text(`Generated on ${new Date().toLocaleString()} | Ivory Home Health Care, Inc.`, pageWidth / 2, 285, { align: 'center' });

  return doc;
};

/**
 * Generate PDF for OASIS Assessment
 */
export const generateOasisPDF = (formData) => {
  const doc = fillablePdf();
  doc.patientInfo = { patientId: formData.patientId, name: formData.patientName, dob: formData.dateOfBirth || formData.patientDOB, mrn: formData.medicalRecordNumber || formData.mrn, dx: formData.primaryDiagnosis || formData.diagnosis };
  const pageWidth = doc.internal.pageSize.getWidth();
  let y = 20;

  // Header
  drawTitledHeader(doc, `OASIS ${formData.assessmentType} Assessment`);
  
  y = 45;
  doc.setTextColor(0, 0, 0);

  // Patient Info
  doc.setFillColor(240, 240, 240);
  doc.rect(15, y, pageWidth - 30, 25, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text('PATIENT INFORMATION', 20, y + 7);
  doc.setFont('helvetica', 'normal');
  doc.text(`Patient: ${formData.patientName || '_______________'}`, 20, y + 15);
  doc.text(`Assessment Date: ${formData.assessmentDate || '_______________'}`, 100, y + 15);
  doc.text(`Physician: ${formData.physicianName || '_______________'}`, 20, y + 22);
  doc.text(`NPI: ${formData.npi || '_______________'}`, 100, y + 22);
  y += 35;

  // BIMS Section
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...BRAND_COLOR);
  doc.text('BIMS - BRIEF INTERVIEW FOR MENTAL STATUS', 20, y);
  doc.setTextColor(0, 0, 0);
  y += 8;
  
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  const bimsItems = [
    ['C0200 - Repetition:', formData.bimsRepetition],
    ['C0300A - Year:', formData.bimsYear],
    ['C0300B - Month:', formData.bimsMonth],
    ['C0300C - Day:', formData.bimsDay],
    ['C0400A - Recall "Sock":', formData.bimsRecallSock],
    ['C0400B - Recall "Blue":', formData.bimsRecallBlue],
    ['C0400C - Recall "Bed":', formData.bimsRecallBed],
  ];
  
  bimsItems.forEach((item, i) => {
    const x = i < 4 ? 20 : 110;
    const yOffset = (i % 4) * 6;
    doc.text(`${item[0]} ${item[1] ?? 'N/A'}`, x, y + yOffset);
  });
  
  y += 28;
  doc.setFont('helvetica', 'bold');
  doc.setFillColor(200, 230, 200);
  doc.rect(20, y - 5, 80, 10, 'F');
  doc.text(`BIMS TOTAL SCORE: ${formData.bimsTotal ?? 'N/A'} / 15`, 25, y + 2);
  y += 15;

  // PHQ-9 Section
  doc.setFontSize(11);
  doc.setTextColor(...BRAND_COLOR);
  doc.text('PHQ-9 - PATIENT HEALTH QUESTIONNAIRE', 20, y);
  doc.setTextColor(0, 0, 0);
  y += 8;
  
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  const phqItems = [
    ['Little interest:', formData.phqLittleInterest],
    ['Feeling down:', formData.phqFeelingDown],
    ['Sleep trouble:', formData.phqSleepTrouble],
    ['Feeling tired:', formData.phqFeelingTired],
    ['Appetite:', formData.phqAppetite],
    ['Feeling bad:', formData.phqFeelingBad],
    ['Concentration:', formData.phqConcentration],
    ['Moving/speaking:', formData.phqMovingSpeaking],
    ['Suicidal thoughts:', formData.phqSuicidalThoughts],
  ];
  
  phqItems.forEach((item, i) => {
    const x = (i % 3) * 60 + 20;
    const yOffset = Math.floor(i / 3) * 6;
    doc.text(`${item[0]} ${item[1] ?? 'N/A'}`, x, y + yOffset);
  });
  
  y += 22;
  doc.setFont('helvetica', 'bold');
  doc.setFillColor(230, 200, 230);
  doc.rect(20, y - 5, 80, 10, 'F');
  doc.text(`PHQ-9 TOTAL SCORE: ${formData.phqTotal ?? 'N/A'} / 27`, 25, y + 2);
  y += 15;

  // Functional Status
  doc.setFontSize(11);
  doc.setTextColor(...BRAND_COLOR);
  doc.text('FUNCTIONAL STATUS', 20, y);
  doc.setTextColor(0, 0, 0);
  y += 8;
  
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text(`Self-Care Score: ${formData.selfCareScore ?? 'N/A'}`, 20, y);
  doc.text(`Mobility Score: ${formData.mobilityScore ?? 'N/A'}`, 80, y);
  doc.text(`Transfer Score: ${formData.transferScore ?? 'N/A'}`, 140, y);
  y += 8;
  doc.text(`Walking Score: ${formData.walkingScore ?? 'N/A'}`, 20, y);
  doc.text(`Pain Level: ${formData.painLevel ?? 'N/A'}/10`, 80, y);
  doc.text(`Fall Risk: ${formData.fallRisk ?? 'N/A'}`, 140, y);
  y += 15;

  // Health Conditions
  doc.setFontSize(11);
  doc.setTextColor(...BRAND_COLOR);
  doc.text('HEALTH CONDITIONS', 20, y);
  doc.setTextColor(0, 0, 0);
  y += 8;
  
  doc.setFontSize(9);
  doc.text(`Height: ${formData.heightInches ?? 'N/A'} in`, 20, y);
  doc.text(`Weight: ${formData.weightLbs ?? 'N/A'} lbs`, 70, y);
  y += 6;
  doc.text(`Pressure Ulcer: ${formData.hasPressureUlcer === true ? 'Yes' : formData.hasPressureUlcer === false ? 'No' : 'N/A'}`, 20, y);
  doc.text(`Stasis Ulcer: ${formData.hasStasisUlcer === true ? 'Yes' : formData.hasStasisUlcer === false ? 'No' : 'N/A'}`, 80, y);
  doc.text(`Surgical Wound: ${formData.hasSurgicalWound === true ? 'Yes' : formData.hasSurgicalWound === false ? 'No' : 'N/A'}`, 140, y);
  y += 15;

  // Homebound Status
  doc.setFontSize(11);
  doc.setTextColor(...BRAND_COLOR);
  doc.text('HOMEBOUND STATUS', 20, y);
  doc.setTextColor(0, 0, 0);
  y += 8;
  
  doc.setFontSize(9);
  if (formData.homeboundCriteriaOne?.length > 0) {
    doc.text(`Criteria 1: ${formData.homeboundCriteriaOne.join(', ')}`, 20, y, { maxWidth: 170 });
    y += 10;
  }
  if (formData.homeboundCriteriaTwo) {
    doc.text(`Criteria 2: ${formData.homeboundCriteriaTwo}`, 20, y, { maxWidth: 170 });
  }

  // Footer
  doc.setFontSize(8);
  doc.setTextColor(128, 128, 128);
  doc.text(`Generated on ${new Date().toLocaleString()} | Ivory Home Health Care, Inc.`, pageWidth / 2, 285, { align: 'center' });

  return doc;
};

/**
 * Generate PDF for Discharge Assessment
 */
export const generateDischargePDF = (formData) => {
  const doc = fillablePdf();
  doc.patientInfo = { patientId: formData.patientId, name: formData.patientName, dob: formData.dateOfBirth || formData.patientDOB, mrn: formData.medicalRecordNumber || formData.mrn, dx: formData.primaryDiagnosis || formData.diagnosis };
  const pageWidth = doc.internal.pageSize.getWidth();
  let y = 20;

  // Header
  drawTitledHeader(doc, 'Discharge Assessment');
  
  y = 45;
  doc.setTextColor(0, 0, 0);

  // Patient Info
  doc.setFillColor(240, 240, 240);
  doc.rect(15, y, pageWidth - 30, 20, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text('PATIENT INFORMATION', 20, y + 7);
  doc.setFont('helvetica', 'normal');
  doc.text(`Patient: ${formData.patientName || '_______________'}`, 20, y + 15);
  doc.text(`Discharge Date: ${formData.dischargeDate || '_______________'}`, 120, y + 15);
  y += 30;

  // Administrative
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...BRAND_COLOR);
  doc.text('ADMINISTRATIVE INFORMATION', 20, y);
  doc.setTextColor(0, 0, 0);
  y += 8;
  
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text(`M0080 Discipline: ${formData.disciplineCompleting || 'N/A'}`, 20, y);
  
  const dispositionMap = {
    1: 'Remained in community (without formal services)',
    2: 'Remained in community (with formal services)',
    3: 'Transferred to non-institutional hospice',
    4: 'Unknown - moved to geographic location not served'
  };
  doc.text(`M2420 Disposition: ${dispositionMap[formData.dischargeDisposition] || 'N/A'}`, 20, y + 6);
  y += 20;

  // Emergent Care
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...BRAND_COLOR);
  doc.text('EMERGENT CARE', 20, y);
  doc.setTextColor(0, 0, 0);
  y += 8;
  
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  const edMap = { 0: 'No', 1: 'Yes, WITHOUT hospital admission', 2: 'Yes, WITH hospital admission' };
  doc.text(`M2301 Emergency Dept Use: ${edMap[formData.usedEmergencyDept] ?? 'N/A'}`, 20, y);
  y += 6;
  
  if (formData.emergentCareReason?.length > 0) {
    doc.text(`M2310 Reason: ${formData.emergentCareReason.join(', ')}`, 20, y);
    y += 6;
  }
  y += 10;

  // Functional Status
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...BRAND_COLOR);
  doc.text('FUNCTIONAL STATUS AT DISCHARGE', 20, y);
  doc.setTextColor(0, 0, 0);
  y += 8;
  
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(`Self-Care Score: ${formData.selfCareScore ?? 'N/A'}`, 20, y);
  doc.text(`Mobility Score: ${formData.mobilityScore ?? 'N/A'}`, 80, y);
  doc.text(`Feeding Score: ${formData.feedingScore ?? 'N/A'}`, 140, y);
  y += 15;

  // Intervention Synopsis
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...BRAND_COLOR);
  doc.text('M2401 INTERVENTION SYNOPSIS', 20, y);
  doc.setTextColor(0, 0, 0);
  y += 8;
  
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(`Fall Prevention: ${formData.fallPreventionIncluded === true ? 'Yes' : formData.fallPreventionIncluded === false ? 'No' : 'N/A'}`, 20, y);
  doc.text(`Depression Intervention: ${formData.depressionIntervention === true ? 'Yes' : formData.depressionIntervention === false ? 'No' : 'N/A'}`, 80, y);
  y += 6;
  doc.text(`Pain Intervention: ${formData.painIntervention === true ? 'Yes' : formData.painIntervention === false ? 'No' : 'N/A'}`, 20, y);
  doc.text(`Pressure Ulcer Prevention: ${formData.pressureUlcerPrevention === true ? 'Yes' : formData.pressureUlcerPrevention === false ? 'No' : 'N/A'}`, 80, y);
  y += 15;

  // Discharge Summary
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...BRAND_COLOR);
  doc.text('DISCHARGE SUMMARY', 20, y);
  doc.setTextColor(0, 0, 0);
  y += 8;
  
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(`Planned Discharge: ${formData.wasPlannedDischarge === true ? 'Yes' : 'No'}`, 20, y);
  doc.text(`Reason: ${formData.reasonForDischarge || 'N/A'}`, 80, y);
  y += 8;
  
  if (formData.dischargeSummary) {
    doc.text('Summary:', 20, y);
    y += 5;
    const lines = doc.splitTextToSize(formData.dischargeSummary, 170);
    doc.text(lines, 20, y);
    y += lines.length * 4 + 5;
  }

  // Signatures
  y += 10;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('SIGNATURES', 20, y);
  y += 10;
  
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.text('Patient/Caregiver:', 20, y);
  doc.line(55, y, 120, y);
  doc.text(formData.patientSignature || '', 57, y - 2);
  doc.text('Clinician:', 125, y);
  doc.line(150, y, 190, y);
  doc.text(formData.clinicianSignature || '', 152, y - 2);

  // Footer
  doc.setFontSize(8);
  doc.setTextColor(128, 128, 128);
  doc.text(`Generated on ${new Date().toLocaleString()} | Ivory Home Health Care, Inc.`, pageWidth / 2, 285, { align: 'center' });

  return doc;
};

/**
 * Generate PDF for Nurses Notes Form
 */
export const generateNursesNotesPDF = (formData) => {
  const doc = fillablePdf();
  doc.patientInfo = { patientId: formData.patientId, name: formData.patientName, dob: formData.dateOfBirth || formData.patientDOB, mrn: formData.medicalRecordNumber || formData.mrn, dx: formData.primaryDiagnosis || formData.diagnosis };
  const pageWidth = doc.internal.pageSize.getWidth();
  let y = 20;

  // Header
  drawTitledHeader(doc, 'Skilled Nursing Visit Notes');
  
  y = 45;
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(10);

  // Patient Info
  doc.setFillColor(240, 240, 240);
  doc.rect(15, y, pageWidth - 30, 25, 'F');
  doc.setFont('helvetica', 'bold');
  doc.text('PATIENT INFORMATION', 20, y + 7);
  doc.setFont('helvetica', 'normal');
  doc.text(`Patient: ${formData.patientName || '_______________'}`, 20, y + 15);
  doc.text(`Visit Date: ${formData.visitDate || '_______________'}`, 100, y + 15);
  doc.text(`MRN: ${formData.medicalRecordNumber || '_______________'}`, 20, y + 22);
  doc.text(`Allergies: ${formData.allergies || 'None'}`, 100, y + 22);
  y += 35;

  // Vital Signs
  doc.setFont('helvetica', 'bold');
  doc.setFillColor(...BRAND_COLOR);
  doc.setTextColor(255, 255, 255);
  doc.rect(15, y, pageWidth - 30, 8, 'F');
  doc.text('VITAL SIGNS', 20, y + 6);
  y += 12;
  doc.setTextColor(0, 0, 0);
  doc.setFont('helvetica', 'normal');
  
  doc.text(`Temp: ${formData.temperature || '__'}°F`, 20, y);
  doc.text(`Pulse: ${formData.pulse || '__'}`, 55, y);
  doc.text(`Resp: ${formData.respiration || '__'}`, 85, y);
  doc.text(`BP: ${formData.bloodPressure || '__/__'}`, 115, y);
  doc.text(`O2 Sat: ${formData.oxygenSaturation || '__'}%`, 150, y);
  y += 7;
  doc.text(`Weight: ${formData.weight || '__'} lbs`, 20, y);
  doc.text(`Blood Sugar: ${formData.bloodSugar || '__'} mg/dL`, 70, y);
  y += 12;

  // Assessment sections
  const sections = [
    { title: 'RESPIRATORY', notes: formData.respiratoryOther },
    { title: 'CARDIOVASCULAR', notes: formData.cardiacOther },
    { title: 'NEUROLOGICAL', notes: formData.neuroOther },
    { title: 'GI/GU', notes: formData.guOther },
    { title: 'SKIN/WOUND', notes: formData.skinOther },
    { title: 'NURSING INTERVENTIONS', notes: formData.interventionsProvided },
    { title: 'PATIENT/CAREGIVER EDUCATION', notes: formData.educationProvided },
    { title: 'PLAN', notes: formData.planForNextVisit }
  ];

  sections.forEach(section => {
    if (y > 260) { doc.addPage(); y = 20; }
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text(section.title, 20, y);
    y += 5;
    doc.setFont('helvetica', 'normal');
    if (section.notes) {
      const lines = doc.splitTextToSize(section.notes, pageWidth - 40);
      doc.text(lines, 20, y);
      y += lines.length * 5 + 5;
    } else {
      y += 10;
    }
  });

  // Signature
  if (y > 240) { doc.addPage(); y = 20; }
  y += 10;
  doc.line(20, y, 90, y);
  doc.line(110, y, 180, y);
  y += 5;
  doc.setFontSize(8);
  doc.text('Nurse Signature', 45, y);
  doc.text('Date', 140, y);

  return doc;
};

/**
 * Generate PDF for Wound Care Flow Sheet
 */
export const generateWoundCarePDF = (formData) => {
  const doc = fillablePdf();
  doc.patientInfo = { patientId: formData.patientId, name: formData.patientName, dob: formData.dateOfBirth || formData.patientDOB, mrn: formData.medicalRecordNumber || formData.mrn, dx: formData.primaryDiagnosis || formData.diagnosis };
  const pageWidth = doc.internal.pageSize.getWidth();
  let y = 20;

  // Header
  drawTitledHeader(doc, 'Wound Care Flow Sheet');
  
  y = 45;
  doc.setTextColor(0, 0, 0);

  // Patient Info
  doc.setFillColor(240, 240, 240);
  doc.rect(15, y, pageWidth - 30, 20, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text('PATIENT INFORMATION', 20, y + 7);
  doc.setFont('helvetica', 'normal');
  doc.text(`Patient: ${formData.patientName || '_______________'}`, 20, y + 15);
  doc.text(`Date: ${formData.startDate || '_______________'}`, 120, y + 15);
  y += 30;

  // Wound Info
  doc.setFont('helvetica', 'bold');
  doc.setFillColor(...BRAND_COLOR);
  doc.setTextColor(255, 255, 255);
  doc.rect(15, y, pageWidth - 30, 8, 'F');
  doc.text('WOUND INFORMATION', 20, y + 6);
  y += 12;
  doc.setTextColor(0, 0, 0);
  doc.setFont('helvetica', 'normal');

  doc.text(`Location: ${formData.woundLocation || '_______________'}`, 20, y);
  doc.text(`Type: ${formData.woundType || '_______________'}`, 110, y);
  y += 7;
  doc.text(`Etiology: ${formData.woundEtiology || '_______________'}`, 20, y);
  doc.text(`Onset Date: ${formData.woundOnsetDate || '_______________'}`, 110, y);
  y += 12;

  // Wound entries
  if (formData.entries && formData.entries.length > 0) {
    doc.setFont('helvetica', 'bold');
    doc.setFillColor(...BRAND_COLOR);
    doc.setTextColor(255, 255, 255);
    doc.rect(15, y, pageWidth - 30, 8, 'F');
    doc.text('WOUND ASSESSMENT ENTRIES', 20, y + 6);
    y += 12;
    doc.setTextColor(0, 0, 0);

    formData.entries.forEach((entry, idx) => {
      if (y > 250) { doc.addPage(); y = 20; }
      doc.setFont('helvetica', 'bold');
      doc.text(`Entry ${idx + 1} - ${entry.date || ''}`, 20, y);
      y += 6;
      doc.setFont('helvetica', 'normal');
      doc.text(`Size: ${entry.length || '__'}L x ${entry.width || '__'}W x ${entry.depth || '__'}D cm`, 20, y);
      doc.text(`Tissue: ${entry.tissueType || '___'}`, 100, y);
      y += 6;
      doc.text(`Drainage: ${entry.drainageAmount || '___'} ${entry.drainageType || ''}`, 20, y);
      doc.text(`Odor: ${entry.odor || 'None'}`, 100, y);
      y += 6;
      doc.text(`Treatment: ${entry.treatment || '_______________'}`, 20, y);
      y += 10;
    });
  }

  // Signature
  y += 15;
  doc.line(20, y, 90, y);
  doc.line(110, y, 180, y);
  y += 5;
  doc.setFontSize(8);
  doc.text('Clinician Signature', 40, y);
  doc.text('Date', 140, y);

  return doc;
};

/**
 * Generate PDF for Face to Face Encounter
 */
export const generateFaceToFacePDF = (formData) => {
  const doc = fillablePdf();
  doc.patientInfo = { patientId: formData.patientId, name: formData.patientName, dob: formData.dateOfBirth || formData.patientDOB, mrn: formData.medicalRecordNumber || formData.mrn, dx: formData.primaryDiagnosis || formData.diagnosis };
  const pageWidth = doc.internal.pageSize.getWidth();
  let y = 20;

  // Header
  drawTitledHeader(doc, 'Face-to-Face Encounter Documentation');
  
  y = 45;
  doc.setTextColor(0, 0, 0);

  // Patient Info
  doc.setFillColor(240, 240, 240);
  doc.rect(15, y, pageWidth - 30, 25, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text('PATIENT & ENCOUNTER INFORMATION', 20, y + 7);
  doc.setFont('helvetica', 'normal');
  doc.text(`Patient: ${formData.patientName || '_______________'}`, 20, y + 15);
  doc.text(`DOB: ${formData.patientDOB || '_______________'}`, 110, y + 15);
  doc.text(`Encounter Date: ${formData.encounterDate || '_______________'}`, 20, y + 22);
  doc.text(`Physician: ${formData.physicianName || '_______________'}`, 110, y + 22);
  y += 35;

  // Clinical Findings
  doc.setFont('helvetica', 'bold');
  doc.setFillColor(...BRAND_COLOR);
  doc.setTextColor(255, 255, 255);
  doc.rect(15, y, pageWidth - 30, 8, 'F');
  doc.text('CLINICAL FINDINGS', 20, y + 6);
  y += 12;
  doc.setTextColor(0, 0, 0);
  doc.setFont('helvetica', 'normal');

  doc.text(`Primary Diagnosis: ${formData.primaryDiagnosis || '_______________'}`, 20, y);
  y += 7;
  
  if (formData.clinicalFindings) {
    const lines = doc.splitTextToSize(`Clinical Findings: ${formData.clinicalFindings}`, pageWidth - 40);
    doc.text(lines, 20, y);
    y += lines.length * 5 + 5;
  }
  y += 5;

  // Homebound Status
  doc.setFont('helvetica', 'bold');
  doc.setFillColor(...BRAND_COLOR);
  doc.setTextColor(255, 255, 255);
  doc.rect(15, y, pageWidth - 30, 8, 'F');
  doc.text('HOMEBOUND STATUS', 20, y + 6);
  y += 12;
  doc.setTextColor(0, 0, 0);
  doc.setFont('helvetica', 'normal');

  doc.rect(20, y - 4, 4, 4);
  if (formData.isHomebound) {
    doc.setFillColor(...BRAND_COLOR);
    doc.rect(20.5, y - 3.5, 3, 3, 'F');
  }
  doc.text('Patient is homebound', 28, y);
  y += 7;

  if (formData.homeboundReason) {
    const lines = doc.splitTextToSize(`Reason: ${formData.homeboundReason}`, pageWidth - 40);
    doc.text(lines, 20, y);
    y += lines.length * 5 + 5;
  }
  y += 10;

  // Skilled Services
  doc.setFont('helvetica', 'bold');
  doc.setFillColor(...BRAND_COLOR);
  doc.setTextColor(255, 255, 255);
  doc.rect(15, y, pageWidth - 30, 8, 'F');
  doc.text('SKILLED SERVICES NEEDED', 20, y + 6);
  y += 12;
  doc.setTextColor(0, 0, 0);
  doc.setFont('helvetica', 'normal');

  const services = [
    { label: 'Skilled Nursing', checked: formData.needsSkilledNursing },
    { label: 'Physical Therapy', checked: formData.needsPhysicalTherapy },
    { label: 'Occupational Therapy', checked: formData.needsOccupationalTherapy },
    { label: 'Speech Therapy', checked: formData.needsSpeechTherapy }
  ];

  services.forEach((service, idx) => {
    const x = 20 + (idx % 2) * 80;
    if (idx === 2) y += 7;
    doc.rect(x, y - 4, 4, 4);
    if (service.checked) {
      doc.setFillColor(...BRAND_COLOR);
      doc.rect(x + 0.5, y - 3.5, 3, 3, 'F');
    }
    doc.text(service.label, x + 8, y);
  });
  y += 15;

  // Physician Certification
  y += 10;
  doc.setFont('helvetica', 'bold');
  doc.text('PHYSICIAN CERTIFICATION', 20, y);
  y += 7;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text('I certify that this patient is homebound and requires the skilled services indicated above.', 20, y);
  y += 15;
  doc.line(20, y, 90, y);
  doc.line(110, y, 180, y);
  y += 5;
  doc.setFontSize(8);
  doc.text('Physician Signature', 40, y);
  doc.text('Date', 140, y);

  return doc;
};

/**
 * Generate PDF for IV Therapy Documentation
 */
export const generateIVTherapyPDF = (formData) => {
  const doc = fillablePdf();
  doc.patientInfo = { patientId: formData.patientId, name: formData.patientName, dob: formData.dateOfBirth || formData.patientDOB, mrn: formData.medicalRecordNumber || formData.mrn, dx: formData.primaryDiagnosis || formData.diagnosis };
  const pageWidth = doc.internal.pageSize.getWidth();
  let y = 20;

  // Header
  drawTitledHeader(doc, 'IV Therapy Documentation');
  
  y = 45;
  doc.setTextColor(0, 0, 0);

  // Patient Info
  doc.setFillColor(240, 240, 240);
  doc.rect(15, y, pageWidth - 30, 25, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text('PATIENT INFORMATION', 20, y + 7);
  doc.setFont('helvetica', 'normal');
  doc.text(`Patient: ${formData.patientName || '_______________'}`, 20, y + 15);
  doc.text(`Date: ${formData.visitDate || '_______________'}`, 110, y + 15);
  doc.text(`MRN: ${formData.medicalRecordNumber || '_______________'}`, 20, y + 22);
  doc.text(`Allergies: ${formData.allergies || 'NKDA'}`, 110, y + 22);
  y += 35;

  // IV Access
  doc.setFont('helvetica', 'bold');
  doc.setFillColor(...BRAND_COLOR);
  doc.setTextColor(255, 255, 255);
  doc.rect(15, y, pageWidth - 30, 8, 'F');
  doc.text('IV ACCESS INFORMATION', 20, y + 6);
  y += 12;
  doc.setTextColor(0, 0, 0);
  doc.setFont('helvetica', 'normal');

  doc.text(`Access Type: ${formData.accessType || '_______________'}`, 20, y);
  doc.text(`Site Location: ${formData.siteLocation || '_______________'}`, 110, y);
  y += 7;
  doc.text(`Catheter Size: ${formData.catheterSize || '___'}G`, 20, y);
  doc.text(`Insertion Date: ${formData.insertionDate || '_______________'}`, 110, y);
  y += 12;

  // Medication/Infusion
  doc.setFont('helvetica', 'bold');
  doc.setFillColor(...BRAND_COLOR);
  doc.setTextColor(255, 255, 255);
  doc.rect(15, y, pageWidth - 30, 8, 'F');
  doc.text('MEDICATION/INFUSION', 20, y + 6);
  y += 12;
  doc.setTextColor(0, 0, 0);
  doc.setFont('helvetica', 'normal');

  doc.text(`Medication: ${formData.medicationName || '_______________'}`, 20, y);
  doc.text(`Dose: ${formData.dose || '_______________'}`, 110, y);
  y += 7;
  doc.text(`Frequency: ${formData.frequency || '_______________'}`, 20, y);
  doc.text(`Rate: ${formData.rate || '_______________'}`, 110, y);
  y += 7;
  doc.text(`Diluent: ${formData.diluent || '_______________'}`, 20, y);
  doc.text(`Volume: ${formData.volume || '_______________'}`, 110, y);
  y += 12;

  // Site Assessment
  doc.setFont('helvetica', 'bold');
  doc.setFillColor(...BRAND_COLOR);
  doc.setTextColor(255, 255, 255);
  doc.rect(15, y, pageWidth - 30, 8, 'F');
  doc.text('SITE ASSESSMENT', 20, y + 6);
  y += 12;
  doc.setTextColor(0, 0, 0);
  doc.setFont('helvetica', 'normal');

  const assessments = ['No redness', 'No swelling', 'No tenderness', 'No drainage', 'Blood return present'];
  assessments.forEach((item, idx) => {
    const x = 20 + (idx % 3) * 60;
    if (idx === 3) y += 7;
    doc.rect(x, y - 4, 4, 4);
    doc.setFillColor(...BRAND_COLOR);
    doc.rect(x + 0.5, y - 3.5, 3, 3, 'F');
    doc.text(item, x + 8, y);
  });
  y += 15;

  // Notes
  if (formData.notes) {
    doc.setFont('helvetica', 'bold');
    doc.text('NURSING NOTES', 20, y);
    y += 6;
    doc.setFont('helvetica', 'normal');
    const lines = doc.splitTextToSize(formData.notes, pageWidth - 40);
    doc.text(lines, 20, y);
    y += lines.length * 5 + 10;
  }

  // Signature
  y += 10;
  doc.line(20, y, 90, y);
  doc.line(110, y, 180, y);
  y += 5;
  doc.setFontSize(8);
  doc.text('RN Signature', 45, y);
  doc.text('Date', 140, y);

  return doc;
};

/**
 * Download the PDF
 */
export const downloadPDF = async (doc, filename) => {
  const mode = doc.fillMode || await chooseFillMode();
  if (!mode) return;
  doc.fillMode = mode;
  doc.save(filename);
  toast.success('PDF downloaded');
};

/**
 * Open PDF in new tab for printing
 */
export const printPDF = (doc) => {
  const pdfBlob = doc.output('blob');
  const url = URL.createObjectURL(pdfBlob);
  window.open(url, '_blank');
};
